import GxBlackButtonSmall from "@/app/components/common/actions/buttons/GxBlackButtonSmall";
import GxPrimarySmallButton from "@/app/components/common/actions/buttons/GxPrimarySmallButton";
import GxRedRejectButtonSmall from "@/app/components/common/actions/buttons/GxRedRejectButtonSmall";
import type { PaymentTransaction } from "@/app/types/payments";
import ShimmerLoadingTwo from "@/app/components/webloading/shimmer/ShimmerLoadingTwo";
import { memo, useEffect } from "react";
import { TransferFrom } from "./TransferFrom";
import { TransferTo } from "./TransferTo";
import { Details } from "./Details";
import { MetroMap } from "./MetroMap";
import { ICON_BASE_PATH } from "@/app/utils/config";
import GxHoldButton from "@/app/components/common/actions/buttons/GxHoldButton";
import ShimmerLoading from "@/app/components/webloading/shimmer/ShimmerLoading";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import { useT } from "@/app/hooks/useT";
import { gxDateTimeFormatLong } from "@/app/utils/helpers/gxDateTimeFormat";
import "./DetailsComponent.scss";
interface DetailsProp {
  transactionDetailsLoader: boolean;
  transaction: PaymentTransaction | undefined;
  handleReject?: () => void;
  handleHold?: () => void;
  handleSentForRepair?: () => void;
  handleApprove?: () => void;
  schedule?: boolean;
  hideFooter?: boolean;
  showEdit?: boolean;
  showSheduledButton?: boolean;
  fromRtp?: boolean;
}

export const DetailsComponent = memo(
  ({
    transactionDetailsLoader,
    transaction,
    handleReject,
    handleSentForRepair,
    handleApprove,
    handleHold,
    schedule = false,
    hideFooter = false,
    showEdit = false,
    showSheduledButton = false,
    fromRtp = false,
  }: DetailsProp) => {
    const t = useT();
    const scrollToFirstError = () => {
      const element = document.getElementById("main_container");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus(); // focus input
      }
    };

    useEffect(() => {
      if (transaction) {
        scrollToFirstError();
      }
    }, [transaction]);

    if (transactionDetailsLoader === true) {
      if (schedule) {
        return <ShimmerLoading />;
      } else {
        return <ShimmerLoadingTwo />;
      }
    }

    return (
      <div id="main_container" className="gx-payment-schedule-container">
        {schedule ? (
          <div className="gx-details-schedule-container">
            <div className="gx-details-avatar">
              <span className="gx-details-avatar-text gx-p12-b">
                {transaction?.beneAccountName?.slice(0, 3)?.toLocaleUpperCase()}
              </span>
            </div>

            <div className="gx-details-schedule-amount">
              <p className="gx-details-schedule-title gx-p27-b">Scheduled</p>
              <div className="gx-details-currency-symbol">
                <p className="gx-details-symbol gx-p16-b">
                  {
                    gxCurrencyFormat(
                      transaction?.debitAmount,
                      transaction?.debitCurrency,
                    ).symbol
                  }
                </p>
              </div>
              <p className="gx-details-amount gx-p27-b">
                {
                  gxCurrencyFormat(
                    transaction?.debitAmount,
                    transaction?.debitCurrency,
                  ).integer
                }
              </p>
              <p className="gx-details-decimal gx-p27-b">
                {
                  gxCurrencyFormat(
                    transaction?.debitAmount,
                    transaction?.debitCurrency,
                  ).decimal
                }
              </p>
            </div>

            <p className="gx-details-bene-name gx-p14-r">
              {transaction?.beneAccountName}
            </p>
            <p className="gx-details-payment-date gx-p10-r">
              {gxDateTimeFormatLong(transaction?.paymentDate)}
            </p>

            <div className="gx-details-actions">
              <div className="gx-details-action-btn">
                <img src={`${ICON_BASE_PATH}refund_icon.svg`} alt="refund" />
                <p className="gx-details-action-text gx-p12-b">{t("refund")}</p>
              </div>
              <div className="gx-details-action-btn">
                <img src={`${ICON_BASE_PATH}share_icon.svg`} alt="share" />
                <p className="gx-details-action-text gx-p12-b">{t("share")}</p>
              </div>
              <div className="gx-details-action-btn">
                <img src={`${ICON_BASE_PATH}question_icon.svg`} alt="issues" />
                <p className="gx-details-action-text gx-p12-b">
                  {t("having_issues")}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="gx-details-payment-card">
            <div className="gx-details-card-body">
              <div className="gx-details-card-header">
                <p className="gx-details-card-title gx-p16-md">
                  {t("payment_amount")}
                </p>
                <p className="gx-details-card-value gx-p20-sb">
                  <span className="gx-details-card-symbol">
                    {
                      gxCurrencyFormat(
                        transaction?.debitAmount,
                        transaction?.debitCurrency,
                      ).symbol
                    }
                  </span>
                  {transaction?.debitAmount &&
                    gxCurrencyFormat(
                      transaction?.debitAmount,
                      transaction?.debitCurrency,
                    ).integer}
                  {transaction?.debitAmount &&
                    gxCurrencyFormat(
                      transaction?.debitAmount,
                      transaction?.debitCurrency,
                    ).decimal}
                </p>
              </div>

              <div className="gx-details-approvals">
                <p className="gx-details-approvals-text gx-p12-m">
                  {t("approvals_required")}:{" "}
                  <span className="gx-details-approvals-completed">
                    {
                      transaction?.paymentMetromap?.approvalDetails
                        ?.approvalsCompleted
                    }
                  </span>
                  {"/"}
                  <span className="gx-details-approvals-required">
                    {
                      transaction?.paymentMetromap?.approvalDetails
                        ?.approvalsRequired
                    }
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="gx-transfer-detail-container">
          {/* Transfer From Section */}
          {transaction && <TransferFrom transaction={transaction} />}

          {/* Transfer To Section */}
          {transaction && <TransferTo transaction={transaction} />}

          {/* Transaction Details Section */}
          {transaction && (
            <Details transaction={transaction} schedule={schedule} />
          )}

          {/* Payment Progress */}
          {transaction?.paymentMetromap && (
            <MetroMap metroMap={transaction?.paymentMetromap} />
          )}
        </div>
        {hideFooter !== true && (
          <div className="gx-d-footer-drawer-container">
            {showEdit === true ? (
              <div className="gx-d-footer-drawer-container-rej-app">
                <div className="gx-approve-resubmit-btn-conatiner">
                  <GxPrimarySmallButton
                    handleSubmit={() => {
                      if (handleApprove) {
                        handleApprove();
                      }
                    }}
                    label={
                      transaction?.paymentStatusCode === 4420
                        ? "Repair Transaction"
                        : "Re-Submit Consent"
                    }
                  />
                </div>
              </div>
            ) : (
              <div
                className={
                  fromRtp === true
                    ? "gx-d-footer-drawer-container-rej-app-1"
                    : "gx-d-footer-drawer-container-rej-app"
                }
              >
                <GxRedRejectButtonSmall
                  onClick={() => {
                    if (handleReject) {
                      handleReject();
                    }
                  }}
                >
                  {t("reject")}
                </GxRedRejectButtonSmall>
                {fromRtp === false && (
                  <GxBlackButtonSmall
                    onClick={() => {
                      if (handleSentForRepair) {
                        handleSentForRepair();
                      }
                    }}
                  >
                    {t("send_for_repair")}
                  </GxBlackButtonSmall>
                )}
                <GxPrimarySmallButton
                  handleSubmit={() => {
                    if (handleApprove) {
                      handleApprove();
                    }
                  }}
                  label={"Approve"}
                />
              </div>
            )}
          </div>
        )}

        {showSheduledButton && (
          <div className="gx-d-footer-drawer-container">
            <div className="gx-d-footer-drawer-container-rej-app">
              <GxRedRejectButtonSmall
                onClick={() => {
                  if (handleReject) {
                    handleReject();
                  }
                }}
              >
                {t("cancel_schedule")}
              </GxRedRejectButtonSmall>

              <GxHoldButton
                onClick={() => {
                  if (handleHold) {
                    handleHold();
                  }
                }}
              >
                {t("hold_schedule")}
              </GxHoldButton>
            </div>
          </div>
        )}
      </div>
    );
  },
);
